import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { useOrganization } from '../OrganizationContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardSidebar from './DashboardSidebar';
import DashboardHeader from './DashboardHeader';
import AgentManager from './AgentManager';
import SessionsList from './SessionsList';
import CostDashboard from './CostDashboard';
import PhoneNumberManager from './PhoneNumberManager';
import TelnyxPhoneManagement from './TelnyxPhoneManagement';
import PhoneNumbersHub from './PhoneNumbersHub';
// Phase 4 Components
import WebhookConfiguration from './WebhookConfiguration';
import ApiInjection from './ApiInjection';
import SessionAnalysis from './SessionAnalysis';
import IvrQaDashboard from './IvrQaDashboard';
import WebhookEvents from './WebhookEvents';
import CustomFunctions from './CustomFunctions';
// Phase 6 Components
import PIIPreview from './PIIPreview';
// Phase 7 Components
import KnowledgeBaseManager from './KnowledgeBaseManager';
import KbOptimizer from './KbOptimizer';
import KbWebScraper from './KbWebScraper';
import WebsiteMonitoring from './WebsiteMonitoring';
import CobrowseManager from './CobrowseManager';
import OutboundApiManager from './OutboundApiManager';
import Workspace from './Workspace';
import Usage from './Usage';
import Integrations from './Integrations';
import CallHistoryExport from './CallHistoryExport';
import ChatHistoryExport from './ChatHistoryExport';
import SuperAdminDashboard from './SuperAdminDashboard';
import ProfilePhoneSettings from './ProfilePhoneSettings';
import { sessions, agents, developers } from '../api';
import { Database, Activity, AlertTriangle, Globe as GlobeIcon, Timer, DollarSign, TrendingUp, Bot, PhoneIncoming, PhoneOutgoing, Clock, User, Building } from 'lucide-react';

// Profile Section Component
const ProfileSection = ({ user, organizations, currentOrg }) => {
  const [developerInfo, setDeveloperInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDeveloperInfo = async () => {
      try {
        const response = await developers.getMe();
        setDeveloperInfo(response.data);
      } catch (error) {
        console.error('Failed to load developer info:', error);
      } finally {
        setLoading(false);
      }
    };
    loadDeveloperInfo();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-1">Profile</h1>
          <p className="text-sm text-muted-foreground">Manage your profile settings</p>
        </div>
        <div className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-4">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  const displayName = developerInfo?.name || user?.name || 'N/A';
  const displayEmail = developerInfo?.email || user?.email || 'N/A';
  const isSuperAdmin = developerInfo?.is_super_admin || user?.is_super_admin || false;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-foreground mb-1">Profile</h1>
        <p className="text-sm text-muted-foreground">Manage your profile settings</p>
      </div>
      
      {/* Developer Information */}
      <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-lg shadow-primary/5">
        <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-primary" />
          Developer Information
        </h2>
        
        <div className="space-y-4">
          {/* Developer Name */}
          <div className="flex items-start gap-4 p-4 bg-background/50 rounded-lg border border-border/50">
            <div className="flex-1">
              <div className="text-sm font-medium text-muted-foreground mb-1">Name</div>
              <div className="text-lg font-semibold text-foreground">
                {displayName}
              </div>
            </div>
          </div>
          
          {/* Email */}
          <div className="flex items-start gap-4 p-4 bg-background/50 rounded-lg border border-border/50">
            <div className="flex-1">
              <div className="text-sm font-medium text-muted-foreground mb-1">Email</div>
              <div className="text-lg font-semibold text-foreground">
                {displayEmail}
              </div>
            </div>
          </div>
          
          {/* Role */}
          <div className={`flex items-start gap-4 p-4 rounded-lg border ${
            isSuperAdmin 
              ? 'bg-purple-500/10 border-purple-500/20' 
              : 'bg-background/50 border-border/50'
          }`}>
            <div className="flex-1">
              <div className="text-sm font-medium text-muted-foreground mb-1">Role</div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 border rounded-md text-sm font-medium ${
                  isSuperAdmin
                    ? 'bg-purple-500/20 text-purple-500 border-purple-500/30'
                    : 'bg-primary/20 text-primary border-primary/30'
                }`}>
                  {isSuperAdmin ? 'Super Admin' : 'Developer'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* SMS login codes */}
      <ProfilePhoneSettings
        initialPhone={developerInfo?.phone_number || null}
        initialSmsAvailable={Boolean(developerInfo?.sms_2fa_available)}
        onSaved={(data) =>
          setDeveloperInfo((prev) => (prev ? { ...prev, ...data } : prev))
        }
      />

      {/* Assigned Organizations */}
      <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-lg shadow-primary/5">
        <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
          <Building className="w-5 h-5 text-primary" />
          Assigned Organizations ({organizations.length})
        </h2>
        
        {organizations.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Building className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No organizations assigned</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {organizations.map((org) => (
              <div
                key={org.id}
                className={`p-4 rounded-lg border transition-all duration-300 ${
                  currentOrg?.id === org.id
                    ? 'bg-primary/10 border-primary/50 shadow-lg shadow-primary/10'
                    : 'bg-background/50 border-border/50 hover:border-primary/30 hover:shadow-md'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="font-semibold text-foreground mb-1">{org.name}</div>
                    {currentOrg?.id === org.id && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/20 text-primary border border-primary/30 rounded-md text-xs font-medium">
                        Current
                      </span>
                    )}
                  </div>
                </div>
                {org.max_minutes && (
                  <div className="text-xs text-muted-foreground mt-2">
                    Limit: {org.max_minutes} minutes
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSectionState] = useState(() => searchParams.get('section') || 'agents');

  // Sync activeSection whenever the URL ?section= param changes (e.g. from navigate() in child components)
  useEffect(() => {
    const section = searchParams.get('section');
    if (section) {
      setActiveSectionState(section);
    }
  }, [searchParams]);

  const setActiveSection = (section) => {
    setActiveSectionState(section);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('section', section);
      if (section !== 'session-analysis') next.delete('sessionId');
      if (section !== 'agents') next.delete('agentId');
      return next;
    }, { replace: true });
  };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [stats, setStats] = useState({
    total_sessions: 0,
    active_sessions: 0,
    abandoned_sessions: 0,
    web_sessions: 0,
    inbound_phone_sessions: 0,
    outbound_phone_sessions: 0,
    avg_duration: 0,
    total_minutes: 0,
    total_cost_usd: 0,
    avg_cost_usd: 0
  });
  const [totalAgents, setTotalAgents] = useState(0);
  const [loadingStats, setLoadingStats] = useState(false);
  const { logout, user } = useAuth();
  const { organizations, currentOrg, switchOrganization } = useOrganization();
  const navigate = useNavigate();

  // Load stats when overview is active
  useEffect(() => {
    if (activeSection === 'overview') {
      loadStats();
      loadAgents();
    }
  }, [activeSection]);

  // Reload data when organization changes
  useEffect(() => {
    if (currentOrg) {
      if (activeSection === 'overview') {
        loadStats();
        loadAgents();
      }
    }
  }, [currentOrg?.id]);

  const loadStats = async () => {
    try {
      setLoadingStats(true);
      const response = await sessions.stats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await agents.list();
      // API returns a list directly, axios wraps it in response.data
      const agentsList = response.data || [];
      setTotalAgents(Array.isArray(agentsList) ? agentsList.length : 0);
    } catch (error) {
      console.error('Failed to load agents:', error);
      setTotalAgents(0);
    }
  };

  // Listen for tab switch events from child components
  React.useEffect(() => {
    const handleSwitchToAnalysis = (event) => {
      const sessionId = event.detail?.sessionId;
      setActiveSectionState('session-analysis');
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('section', 'session-analysis');
        if (sessionId) next.set('sessionId', sessionId);
        return next;
      }, { replace: true });
    };

    const handleNavigateToFunctions = (event) => {
      setActiveSection('custom-functions');
    };

    window.addEventListener('switchToAnalysisTab', handleSwitchToAnalysis);
    window.addEventListener('navigateToFunctions', handleNavigateToFunctions);
    
    return () => {
      window.removeEventListener('switchToAnalysisTab', handleSwitchToAnalysis);
      window.removeEventListener('navigateToFunctions', handleNavigateToFunctions);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    // Redirect based on user type
    if (user?.is_super_admin || user?.user_type === 'super_admin') {
      navigate('/super-admin/login');
    } else {
      navigate('/developer/login');
    }
  };

  const handleOrgSwitch = async (orgId) => {
    try {
      await switchOrganization(orgId);
    } catch (error) {
      console.error('Failed to switch organization:', error);
    }
  };

  // Map sidebar sections to components
  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
  return (
          <div className="space-y-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-1">Overview</h1>
              <p className="text-sm text-muted-foreground">Dashboard overview and analytics</p>
            </div>
            {loadingStats ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {/* Total Agents */}
                <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4 animate-slide-in-up">
                  <div className="flex items-center gap-2.5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                      <Bot className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xl font-bold text-foreground">
                        {totalAgents}
                      </div>
                      <div className="text-xs text-muted-foreground">Total Agents</div>
                    </div>
                  </div>
                </div>

                {/* Total Sessions */}
                <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                  <div className="flex items-center gap-2.5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                      <Database className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xl font-bold text-foreground">
                        {stats.total_sessions}
                      </div>
                      <div className="text-xs text-muted-foreground">Total Sessions</div>
                    </div>
                  </div>
                </div>
                
                {/* Active Sessions */}
                <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                  <div className="flex items-center gap-2.5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                      <Activity className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xl font-bold text-success">
                        {stats.active_sessions}
                      </div>
                      <div className="text-xs text-muted-foreground">Active Now</div>
                    </div>
                  </div>
                </div>
                
                {/* Abandoned Sessions */}
                <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                  <div className="flex items-center gap-2.5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                      <AlertTriangle className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xl font-bold text-warning">
                        {stats.abandoned_sessions}
                      </div>
                      <div className="text-xs text-muted-foreground">Abandoned</div>
                    </div>
                  </div>
                </div>
                
                {/* Web Sessions */}
                <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                  <div className="flex items-center gap-2.5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                      <GlobeIcon className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xl font-bold text-primary">
                        {stats.web_sessions}
                      </div>
                      <div className="text-xs text-muted-foreground">Web Sessions</div>
                    </div>
                  </div>
                </div>
                
                {/* Avg Duration */}
                <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                  <div className="flex items-center gap-2.5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                      <Timer className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xl font-bold text-secondary-cyan">
                        {stats.avg_duration}s
                      </div>
                      <div className="text-xs text-muted-foreground">Avg Duration</div>
                    </div>
                  </div>
                </div>
                
                {/* Total Cost */}
                <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                  <div className="flex items-center gap-2.5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                      <DollarSign className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xl font-bold text-success">
                        ${(stats.total_cost_usd || 0).toFixed(4)}
                      </div>
                      <div className="text-xs text-muted-foreground">Total Cost</div>
                    </div>
                  </div>
                </div>
                
                {/* Avg Cost */}
                <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                  <div className="flex items-center gap-2.5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                      <TrendingUp className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xl font-bold text-primary">
                        ${(stats.avg_cost_usd || 0).toFixed(4)}
                      </div>
                      <div className="text-xs text-muted-foreground">Avg Cost</div>
                    </div>
                  </div>
                </div>
                
                {/* Total Minutes */}
                <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                  <div className="flex items-center gap-2.5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                      <Clock className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xl font-bold text-secondary-cyan">
                        {(stats.total_minutes || 0).toFixed(2)}
                      </div>
                      <div className="text-xs text-muted-foreground">Total Minutes</div>
                    </div>
                  </div>
                </div>
                
                {/* Inbound Phone Sessions */}
                <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                  <div className="flex items-center gap-2.5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                      <PhoneIncoming className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xl font-bold text-success">
                        {stats.inbound_phone_sessions || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Inbound Calls</div>
                    </div>
                  </div>
            </div>
            
                {/* Outbound Phone Sessions */}
                <div className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-4">
                  <div className="flex items-center gap-2.5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                      <PhoneOutgoing className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xl font-bold text-primary">
                        {stats.outbound_phone_sessions || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Outbound Calls</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 'agents':
        return <AgentManager />;
      case 'conversations':
        return <SessionsList />;
      case 'call-history-export':
        return <CallHistoryExport />;
      case 'chat-history-export':
        return <ChatHistoryExport />;
      case 'phone-numbers':
        return <PhoneNumbersHub />;
      case 'profile':
        return <ProfileSection user={user} organizations={organizations} currentOrg={currentOrg} />;
      case 'workspace':
        return <Workspace onSectionChange={setActiveSection} />;
      // Additional sections from old tabs
      case 'costs':
        return <CostDashboard />;
      case 'webhooks':
        return <WebhookConfiguration />;
      case 'custom-functions':
        return <CustomFunctions />;
      case 'api-injection':
        return <ApiInjection />;
      case 'session-analysis':
        return <SessionAnalysis />;
      case 'ivr-qa':
        return <IvrQaDashboard />;
      case 'webhook-events':
        return <WebhookEvents />;
      case 'outbound-api':
        return <OutboundApiManager />;
      case 'pii-preview':
        return <PIIPreview />;
      case 'knowledge-base':
        return <KnowledgeBaseManager />;
      case 'kb-optimizer':
        return <KbOptimizer />;
      case 'kb-web-scraper':
        return <KbWebScraper />;
      case 'website-monitoring':
        return <WebsiteMonitoring />;
      case 'cobrowse':
        return <CobrowseManager />;
      case 'usage':
        return <Usage />;
      case 'integrations':
        return <Integrations />;
      case 'agents':
        return <AgentManager showArchived={false} />;
      case 'archived-agents':
        return <AgentManager showArchived={true} />;
      default:
        return <AgentManager showArchived={false} />;
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <div className="hidden lg:block fixed left-0 top-0 bottom-0 z-40">
        <DashboardSidebar 
          activeSection={activeSection} 
          onSectionChange={setActiveSection}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed left-0 top-0 bottom-0 w-64">
            <DashboardSidebar 
              activeSection={activeSection} 
              onSectionChange={(section) => {
                setActiveSection(section);
                setMobileMenuOpen(false);
              }} 
            />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {/* Header */}
        <DashboardHeader 
          onMenuClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          sidebarCollapsed={sidebarCollapsed}
          onLogout={handleLogout}
        />

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 pt-20">
          <div className="max-w-7xl mx-auto">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
